///
/// EarthBrowser
///
/// Created by Matt Giger on 9/11/2026
/// Copyright (c) 2026 Geodata Labs
///

import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { wgs } from "@/earthbrowser/utils/geographic"
import { type Perspective } from "@stovokor/camera/Perspective"
import { Renderer } from "@stovokor/engine/Renderer"
import { RenderState } from "@stovokor/engine/RenderState"
import { rgba, type color } from "@stovokor/math/color"
import { type matrix } from "@stovokor/math/matrix"
import { vec2, type vec3 } from "@stovokor/math/vector"
import { type Geometry } from "@stovokor/shader/Geometry"
import { type Material } from "@stovokor/shader/Material"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { type StovokorContext } from "@stovokor/StovokorContext"

// Lift lines above their sampled terrain height proportionally to camera
// altitude so residual error in the medium-res base elevation grid doesn't
// bury them, while hugging the surface when close (km)
const cLiftFactor = 0.004
const cMinLift = 0.05
const cMaxLift = 10.0

// Depth-only bias (km toward the camera) covering the residual between the
// base elevation grid sampled per vertex and the true terrain tiles. Scales
// with altitude: small when zoomed in so ridges occlude roads behind them,
// larger when zoomed out where coarse subdivision undersamples the terrain
const cDepthBiasFactor = 0.03
const cMinDepthBias = 0.5
const cMaxDepthBias = 12.0

// Night-side dimming follows the earth tiles' day/night ramp (EarthTileRenderer
// altitudeScale): the imagery is only darkened above this altitude (km) and
// fully by this much higher, so lines dim over the same band
const cNightRampStartKm = 1500
const cNightRampRangeKm = 3000

// Defaults for optional style fields
const cDefaultCasingWidth = 1.0
const cDefaultCasingColor = rgba(0, 0, 0, 0.3)
const cDefaultNightOpacity = 0.45
const cSolid = new vec2(0, 0)

/** Appearance of one batch of geographic lines */
export interface GeoPolylineStyle {
	color: color
	/** Core line width in CSS pixels */
	width: number
	/**
	 * Soft shadow band drawn outside the core, in CSS pixels per side, fading
	 * out from the core edge. Separates the line from bright imagery without
	 * the hard outline of a solid casing (default 1)
	 */
	casingWidth?: number
	/** Casing color at the core edge (default translucent black) */
	casingColor?: color
	/**
	 * Opacity multiplier on the night side of the terminator, applied over
	 * the same altitude ramp the terrain uses to darken (default 0.45)
	 */
	nightOpacity?: number
	/**
	 * Dash and gap lengths in CSS pixels, constant on screen at any zoom.
	 * Omit for a solid line
	 */
	dash?: [number, number]
	/**
	 * Fade lines by local density: fully hidden where the mean on-screen
	 * spacing between lines is below [0] px, fully drawn above [1] px. The
	 * geometry must carry a density attribute (VectorTileLayerNode builds it)
	 */
	densityFade?: [number, number]
}

/**
 * Material for a style bucket of batched geographic lines
 */
export class GeoPolylineMaterial implements Material {
	public available = true
	public transparency = true
	/** Per-frame opacity multiplier (altitude cross-fades), set by the owning node */
	public opacity = 1

	constructor(public style: GeoPolylineStyle) {}
}

/**
 * Renderer for batched geographic polylines with screen-space width.
 * Line width and color are per-bucket uniforms set from the material.
 */
export class GeoPolylineRenderer extends Renderer {
	public modelView = new UniformDynamic<matrix>()
	public projection = new UniformDynamic<matrix>()
	public viewport = new UniformDynamic<vec2>()
	public nearPlane = new UniformDynamic<number>()
	public lineWidth = new UniformDynamic<number>()
	public casingWidth = new UniformDynamic<number>()
	public lift = new UniformDynamic<number>()
	public depthBias = new UniformDynamic<number>()
	public color = new UniformDynamic<color>()
	public casingColor = new UniformDynamic<color>()
	public dashPattern = new UniformDynamic<vec2>()
	public densityFade = new UniformDynamic<vec2>()
	public opacity = new UniformDynamic<number>()
	public cameraAltitude = new UniformDynamic<number>()
	public lightDirection = new UniformDynamic<vec3>()
	public nightDimming = new UniformDynamic<number>()
	public elevationMultiplier = new UniformDynamic<number>()

	private material: GeoPolylineMaterial
	private ebContext: EarthBrowserContext

	constructor(context: StovokorContext, geometry: Geometry, material: GeoPolylineMaterial) {
		super(context, new ShaderSourceDefinition("geopolyline"), geometry, [material])
		this.material = material
		this.ebContext = context as EarthBrowserContext

		// Lines are overlays: never write depth so they can't occlude other
		// transparent objects, but still depth-test against the globe
		this.drawState.depthWrite = false
	}

	protected stateUpdate(state: RenderState): void {
		this.modelView.value = state.modelView
		this.projection.value = state.projection
		this.viewport.value = new vec2(state.width, state.height)
		this.nearPlane.value = (state.camera.projection as Perspective).near ?? 0.01

		const altitude = state.camera.position.length - wgs.a
		const lift = Math.min(Math.max(altitude * cLiftFactor, cMinLift), cMaxLift)
		this.lift.value = lift
		this.depthBias.value = Math.min(Math.max(altitude * cDepthBiasFactor, cMinDepthBias), cMaxDepthBias) + lift

		const style = this.material.style
		this.lineWidth.value = style.width
		this.casingWidth.value = style.casingWidth ?? cDefaultCasingWidth
		this.color.value = style.color
		this.casingColor.value = style.casingColor ?? cDefaultCasingColor
		this.dashPattern.value = style.dash ? new vec2(style.dash[0], style.dash[1]) : cSolid
		this.densityFade.value = style.densityFade ? new vec2(style.densityFade[0], style.densityFade[1]) : cSolid
		this.opacity.value = this.material.opacity
		this.cameraAltitude.value = altitude
		this.lightDirection.value = RenderState.lightDirection

		// How much of the night-side dimming applies at this altitude, scaled
		// by how far the style wants to dim
		const nightRamp = Math.min(Math.max((altitude - cNightRampStartKm) / cNightRampRangeKm, 0), 1)
		this.nightDimming.value = nightRamp * (1 - (style.nightOpacity ?? cDefaultNightOpacity))
		this.elevationMultiplier.value = this.ebContext.terrainExaggeration
	}
}
